import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  MenuItem,
  Menu,
  Select,
  Snackbar,
  Alert,
} from "@mui/material";
import emailjs from "@emailjs/browser"; // Updated import
import {
  ContactUsContainerStyles,
  ContactUsPaperStyles,
  menuItemStyles,
  menuContainerStyles,
  selectBaseStyles,
  textfieldLayout,
  contentScroll,
} from "./ContactUsStyles";
import { useSelector } from "react-redux";
import colors from "../../../Constants/colors";
import { sendSupportRequest } from "../../../utils/emailService";
import { inputStyles } from "../../../Constants/inputStyles";
import theme from "../../../Constants/theme";
const ContactUs = () => {
  const { user } = useSelector((state) => state.auth);
  const { organization } = useSelector((state) => state.organization);
  const [snackbarOpen, setSnackbarOpen] = useState(false); // For snackbar
  const [snackbarMessage, setSnackbarMessage] = useState(""); // Message for snackbar
  const [snackbarSeverity, setSnackbarSeverity] = useState("info"); // Severity of the alert

  const [formData, setFormData] = useState({
    name: "",
    organization: "",
    queryType: "General Inquiry",
    queryDescription: "",
    priority: "",
  });

  useEffect(() => {
    const username = user?.user?.first_name + " " + user?.user?.last_name;
    const org = organization?.name;
    const priority =
      organization?.subscription === "starter" ? "Normal" : "Important";
    setFormData({ ...formData, name: username, organization: org, priority });
  }, []);

  const queryTypes = [
    "General Inquiry",
    "Technical Support",
    "Billing",
    "Feedback",
    "Request Feature",
  ];

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Open snackbar with a message and severity
  const showSnackbar = (message, severity = "info") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Close snackbar
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { name, organization, queryType, queryDescription, priority } =
      formData;

    try {
      await sendSupportRequest({
        name,
        fromEmail: user?.user?.email,
        organization,
        queryType,
        queryDescription,
        priority,
      });

      showSnackbar("Your query has been sent successfully!", "success");
      setFormData((fd) => ({
        ...fd,
        queryType: "General Inquiry",
        queryDescription: "",
      }));
    } catch (err) {
      console.error("Error sending support email:", err);
      showSnackbar(
        "There was an error sending your query. Please try again.",
        "error"
      );
    }
  };

  return (
    <Container sx={ContactUsContainerStyles}>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Box sx={ContactUsPaperStyles}>
        <Box sx={contentScroll}>
          <form onSubmit={handleSubmit}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                mb: 2,
                justifyContent: "flex-start",
              }}
            >
              {/* Name Field */}
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ color: theme.textColors.dark.primary, mb: 1 }}
                >
                  Name
                </Typography>
                <TextField
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  fullWidth
                  margin="dense"
                  variant="outlined"
                  sx={{
                    ...inputStyles.textField,
                    backgroundColor: theme.backgroundColors.dark.input,
                  }}
                />
              </Box>
              {/* Organization Field */}
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ color: theme.textColors.dark.primary, mb: 1 }}
                >
                  Organization
                </Typography>
                <TextField
                  name="organization"
                  value={formData.organization}
                  onChange={handleInputChange}
                  fullWidth
                  margin="dense"
                  variant="outlined"
                  sx={{
                    ...inputStyles.textField,
                    backgroundColor: theme.backgroundColors.dark.input,
                  }}
                  disabled
                />
              </Box>
              {/* Query Type Field */}
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ color: theme.textColors.dark.primary, mb: 1 }}
                >
                  Query Type
                </Typography>
                <Select
                  name="queryType"
                  value={formData.queryType}
                  onChange={handleInputChange}
                  variant="outlined"
                  fullWidth
                  sx={{ ...inputStyles.select, mt: 1 }}
                  MenuProps={inputStyles.selectMenuProps}
                >
                  {queryTypes.map((type) => (
                    <MenuItem
                      key={type}
                      value={type}
                      sx={{
                        ...menuItemStyles,
                        backgroundColor:
                          formData.queryType === type
                            ? colors.secondaryBlue
                            : "transparent",
                        fontWeight:
                          formData.queryType === type ? "bold" : "normal",
                      }}
                    >
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>
            {/* Query Description */}
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                sx={{ color: theme.textColors.dark.primary, mb: 1 }}
              >
                Query Description
              </Typography>
              <TextField
                name="queryDescription"
                value={formData.queryDescription}
                onChange={handleInputChange}
                multiline
                rows={16}
                fullWidth
                margin="dense"
                variant="outlined"
                sx={inputStyles.textField}
              />
            </Box>
            {/* Submit Button */}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={formData.queryDescription.trim() === ""}
              sx={{
                backgroundColor: colors.primaryBlue,
                "&:hover": {
                  backgroundColor: colors.secondaryBlue,
                },
                "&:disabled": {
                  backgroundColor: colors.darkGray,
                  color: colors.lightGray,
                },
              }}
            >
              Send
            </Button>
          </form>
        </Box>
      </Box>
    </Container>
  );
};

export default ContactUs;
